/*
  PLP behaviour: filtering, sorting, the desktop filter-rail toggle, the mobile
  Filters | Sort drawers, the dual-handle price slider and infinite scroll.

  Filtering goes through Shopify's native filter URLs (`filter.v.*`,
  `filter.p.*`, `sort_by`) and re-renders this section via `?section_id=`,
  so the markup stays in step with whatever the merchant configures.
*/

const PLP_DEBOUNCE = 350;

/* How far the page scrolls before the mobile Filters | Sort bar slides in. */
const PLP_STICKY_BAR_OFFSET = 100;

function plpDebounce(fn, wait) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), wait);
  };
}

class PlpCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.abortController = null;

    this.onFacetChange = plpDebounce(() => this.applyFilters(), PLP_DEBOUNCE);

    this.addEventListener('change', this.handleChange);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('input', this.handleInput);
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('focusin', this.handlePriceFocusIn);
    this.addEventListener('focusout', this.handlePriceFocusOut);
    document.addEventListener('click', this.handleDocumentClick);

    this.bindPriceRanges();
    this.bindInfiniteScroll();
    this.bindScrollPerformance();
    this.syncAppliedOverflow();

    window.addEventListener('scroll', this.syncStickyBar, { passive: true });
    this.syncStickyBar();
  }

  /* The mobile Filters | Sort bar stays out of the way on load, slides in
     once the shopper starts scrolling, and slides back out once the end of
     the listing scrolls into view so it never sits over the footer (CSS in
     plp.css). The class lives on this host element because re-renders only
     replace its innerHTML. */
  syncStickyBar = () => {
    const listingContinuesBelow = this.getBoundingClientRect().bottom > window.innerHeight;
    this.classList.toggle(
      'plp--sticky-bar-visible',
      window.scrollY > PLP_STICKY_BAR_OFFSET && listingContinuesBelow
    );
  };

  /* The chip strip's scroll fade is only meaningful when there are more
     chips than fit; otherwise it just sits next to the last chip as a
     smudge. */
  syncAppliedOverflow() {
    this.querySelectorAll('.plp-applied__scroller').forEach((scroller) => {
      const track = scroller.querySelector('.plp-applied__track');
      if (!track) return;
      scroller.classList.toggle(
        'plp-applied__scroller--overflowing',
        track.scrollWidth > track.clientWidth + 1
      );
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.syncStickyBar);
    if (this.observer) this.observer.disconnect();
  }

  /* ---------------------------------------------------------------- *
   * Events
   * ---------------------------------------------------------------- */

  handleChange = (event) => {
    const target = event.target;
    if (target.closest('[data-plp-facet-form]')) {
      // Price handles apply on 'change' (fires once, on release) rather than
      // 'input' (fires continuously while dragging) — see handleInput for
      // why triggering a request on every drag/keystroke is unsafe here.
      this.onFacetChange();
    }
  };

  handleInput = (event) => {
    const target = event.target;
    if (target.matches('[data-plp-price-handle]')) {
      // Live visual feedback only (handle position, fill bar, paired text
      // field) — the actual filter request waits for 'change' (handleChange
      // above), which fires once when the drag ends. Triggering it here, on
      // every 'input' tick while dragging, would let a mid-drag debounced
      // response swap out the very slider node the pointer is still
      // grabbing, breaking the gesture.
      this.syncPriceRange(target.closest('[data-plp-price-range]'), target, true);
    }
    if (target.matches('[data-plp-price-input]')) {
      // Digit-only enforcement: strip anything that isn't 0-9 as the user
      // types (or pastes), so a letter or symbol literally never appears.
      const caret = target.selectionStart;
      const before = target.value;
      const digitsOnly = before.replace(/[^0-9]/g, '');
      if (digitsOnly !== before) {
        target.value = digitsOnly;
        const removed = before.length - digitsOnly.length;
        const pos = Math.max(0, (caret || digitsOnly.length) - removed);
        target.setSelectionRange(pos, pos);
      }
      // enforceOrder: false — mid-keystroke, a partial number typed into one
      // field must never shove the other field/handle around; blur (below)
      // reconciles min<=max once editing is actually finished.
      //
      // No onFacetChange() here, deliberately: that triggers a debounced
      // network request whose response replaces this whole facets DOM
      // (including the very field being typed into). Firing it per
      // keystroke meant that if the debounce landed mid-number — entirely
      // possible on any normal typing cadence — the field the customer was
      // still typing into got swapped for a freshly-rendered one reflecting
      // a partial value ("2" of "200000"), which looks exactly like the
      // field is randomly refilling itself. handlePriceFocusOut triggers
      // the actual filter request once, after editing is done.
      this.syncPriceInputs(target.closest('[data-plp-price-range]'), target, false);
    }
  };

  /* Strip currency/comma formatting to plain digits while the field is being
     edited, so the user types against clean digits rather than a moving
     "₹" prefix and thousands separators. */
  handlePriceFocusIn = (event) => {
    const target = event.target;
    if (!target.matches('[data-plp-price-input]')) return;
    target.value = this.parsePrice(target.value) || '';
  };

  /* Clamp to the slider's configured bounds and restore the "₹" + grouped
     formatting shown in Figma once the merchant/customer is done typing. */
  handlePriceFocusOut = (event) => {
    const target = event.target;
    if (!target.matches('[data-plp-price-input]')) return;
    const range = target.closest('[data-plp-price-range]');
    if (!range) return;

    const handle = range.querySelector(
      `[data-plp-price-handle="${target.dataset.plpPriceInput}"]`
    );
    const min = Number(handle && handle.min) || 0;
    const max = Number(handle && handle.max) || 0;
    const clamped = Math.min(Math.max(this.parsePrice(target.value), min), max);

    target.value = this.formatPrice(range, clamped);
    // enforceOrder: true — editing is done; reconcile min<=max now.
    this.syncPriceInputs(range, target, true);
    // The filter request fires from here, once editing is actually
    // finished, rather than per keystroke in handleInput (see the comment
    // there for why).
    this.onFacetChange();
  };

  handleClick = (event) => {
    const sortTrigger = event.target.closest('[data-plp-sort-trigger]');
    if (sortTrigger) {
      this.toggleSort(sortTrigger);
      return;
    }

    const sortOption = event.target.closest('[data-plp-sort-option]');
    if (sortOption) {
      event.preventDefault();
      this.applySort(sortOption.value);
      return;
    }

    const filterToggle = event.target.closest('[data-plp-filter-toggle]');
    if (filterToggle) {
      this.toggleFilterRail(filterToggle);
      return;
    }

    const remove = event.target.closest('[data-plp-facet-remove]');
    if (remove) {
      event.preventDefault();
      this.renderFromUrl(remove.href);
      return;
    }

    const wishlist = event.target.closest('[data-plp-wishlist]');
    if (wishlist) {
      this.toggleWishlist(wishlist);
      return;
    }

    const openDrawer = event.target.closest('[data-plp-open-drawer]');
    if (openDrawer) {
      this.openDrawer(openDrawer.dataset.plpOpenDrawer);
      return;
    }

    if (event.target.closest('[data-plp-close-drawer]')) {
      this.closeDrawers();
    }
  };

  handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    this.closeDrawers();
    this.closeSort();
  };

  handleDocumentClick = (event) => {
    if (!this.contains(event.target)) this.closeSort();
    else if (!event.target.closest('[data-plp-sort]')) this.closeSort();
  };

  /* ---------------------------------------------------------------- *
   * Sort
   * ---------------------------------------------------------------- */

  toggleSort(trigger) {
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (panel) panel.hidden = expanded;
  }

  closeSort() {
    this.querySelectorAll('[data-plp-sort-trigger][aria-expanded="true"]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
    });
  }

  applySort(value) {
    const params = this.currentParams();
    params.set('sort_by', value);
    params.delete('page');
    this.render(params);
  }

  /* ---------------------------------------------------------------- *
   * Filter rail (Figma "Show Filter" / "Hide Filter" states)
   * ---------------------------------------------------------------- */

  toggleFilterRail(button) {
    const hidden = this.classList.toggle('plp--filters-hidden');
    button.setAttribute('aria-expanded', String(!hidden));
    const label = button.querySelector('[data-plp-filter-toggle-label]');
    if (label) label.textContent = hidden ? 'Show Filters' : 'Hide Filters';
    try {
      localStorage.setItem('plp:filters-hidden', hidden ? '1' : '0');
    } catch (error) {
      /* storage unavailable — the toggle still works for this page view */
    }
  }

  /* ---------------------------------------------------------------- *
   * Save / wishlist
   *
   * Figma draws a heart on the card, so this is a save toggle rather than an
   * add-to-cart. Only the pressed state is handled here; persisting it needs a
   * wishlist app or customer metafield, and the `plp:wishlist` event is the
   * hook for that.
   * ---------------------------------------------------------------- */

  toggleWishlist(button) {
    const pressed = button.getAttribute('aria-pressed') === 'true';
    button.setAttribute('aria-pressed', String(!pressed));
    button.dispatchEvent(
      new CustomEvent('plp:wishlist', {
        bubbles: true,
        detail: {
          productId: button.dataset.productId,
          handle: button.dataset.productHandle,
          saved: !pressed,
        },
      })
    );
  }

  /* ---------------------------------------------------------------- *
   * Price range
   * ---------------------------------------------------------------- */

  bindPriceRanges() {
    this.querySelectorAll('[data-plp-price-range]').forEach((range) => {
      this.syncPriceRange(range, null, true);
    });
  }

  /* "20000" -> "₹20,000" (Indian digit grouping, matching Shopify's own
     money filter output for this shop). */
  formatPrice(range, value) {
    const symbol = (range && range.dataset.currency) || '';
    const num = Math.round(Number(value) || 0);
    return `${symbol}${num.toLocaleString('en-IN')}`;
  }

  /* "₹20,000" / "20000" / "" -> 20000. Strips everything but digits, so it
     safely accepts a formatted display value, a raw digit string, or
     whatever's mid-edit in the field. */
  parsePrice(value) {
    return Number(String(value || '').replace(/[^0-9]/g, '')) || 0;
  }

  /* @param changed - whichever <input type="range"> handle should "win" a
       min>max conflict (pushing the other one along with it), only enforced
       when `enforceOrder` is true.
     @param enforceOrder - true on a slider drag and once text-field editing
       is finished (blur); false while a field is still being typed into.
       Left false mid-keystroke so a partial number (e.g. "2" on the way to
       typing "200000") can't shove the *other* field/handle around — the
       fill bar still tracks the live, possibly-inverted values as-is, and
       blur reconciles everything into a valid min<=max state in one step.
     Both text fields are reformatted with "₹" + grouping after every
     change, except whichever one is `document.activeElement` — the
     customer's own keystrokes there are left alone rather than clobbered
     mid-edit by a reformat. */
  syncPriceRange(range, changed, enforceOrder) {
    if (!range) return;
    const min = range.querySelector('[data-plp-price-handle="min"]');
    const max = range.querySelector('[data-plp-price-handle="max"]');
    if (!min || !max) return;

    let minValue = Number(min.value);
    let maxValue = Number(max.value);

    if (enforceOrder && minValue > maxValue) {
      if (changed === max) {
        minValue = maxValue;
        min.value = String(minValue);
      } else {
        // Default covers both "min handle/field changed" and the initial,
        // no-`changed` bind call — pushing max up is the safer assumption
        // since it never silently discards what the customer just entered.
        maxValue = minValue;
        max.value = String(maxValue);
      }
    }

    // The slider's own floor/ceiling (Figma: ₹10,000–₹500,000 by default,
    // merchant-configurable) — not 0, so the fill position is computed
    // relative to that span, not from zero.
    const floor = Number(min.min) || 0;
    const ceiling = Number(min.max) || floor + 1;
    const span = ceiling - floor || 1;
    const fill = range.querySelector('[data-plp-price-fill]');
    if (fill) {
      fill.style.left = `${((minValue - floor) / span) * 100}%`;
      fill.style.right = `${100 - ((maxValue - floor) / span) * 100}%`;
    }

    const minInput = range.querySelector('[data-plp-price-input="min"]');
    const maxInput = range.querySelector('[data-plp-price-input="max"]');
    if (minInput && document.activeElement !== minInput) {
      minInput.value = this.formatPrice(range, minValue);
    }
    if (maxInput && document.activeElement !== maxInput) {
      maxInput.value = this.formatPrice(range, maxValue);
    }
  }

  syncPriceInputs(range, sourceInput, enforceOrder) {
    if (!range) return;
    const min = range.querySelector('[data-plp-price-handle="min"]');
    const max = range.querySelector('[data-plp-price-handle="max"]');
    const minInput = range.querySelector('[data-plp-price-input="min"]');
    const maxInput = range.querySelector('[data-plp-price-input="max"]');
    // Each field only ever drives its own handle here — clamped to that
    // handle's own min/max, never compared against the other one (that
    // comparison is exactly what `enforceOrder` gates in syncPriceRange).
    if (min && minInput && (enforceOrder || sourceInput === minInput)) {
      min.value = String(this.parsePrice(minInput.value) || min.min);
    }
    if (max && maxInput && (enforceOrder || sourceInput === maxInput)) {
      max.value = String(this.parsePrice(maxInput.value) || max.max);
    }

    const sourceHandle =
      sourceInput &&
      range.querySelector(`[data-plp-price-handle="${sourceInput.dataset.plpPriceInput}"]`);
    this.syncPriceRange(range, sourceHandle, enforceOrder);
  }

  /* ---------------------------------------------------------------- *
   * Rendering
   * ---------------------------------------------------------------- */

  currentParams() {
    return new URLSearchParams(window.location.search);
  }

  applyFilters() {
    const params = new URLSearchParams();

    // The sidebar and mobile drawer each render their own independent copy
    // of the facets form — both always exist in the DOM (the inactive one
    // is only hidden via CSS, e.g. the drawer sits behind `display: none`
    // until opened), so reading every `[data-plp-facet-form]` would merge
    // in the *other*, stale copy's checkboxes too. That's what made
    // unchecking a box in the sidebar look like it didn't work: the
    // drawer's still-checked copy of the same filter re-added it right
    // back. `getClientRects().length` is 0 for anything hidden via
    // `display: none` on itself or an ancestor — including the drawer,
    // despite it being `position: fixed` (which defeats the more common
    // `offsetParent` check) — so this reliably picks only the form the
    // customer can actually see and interact with.
    const forms = Array.from(this.querySelectorAll('[data-plp-facet-form]')).filter(
      (form) => form.getClientRects().length > 0
    );
    const activeForms = forms.length
      ? forms
      : Array.from(this.querySelectorAll('[data-plp-facet-form]'));

    activeForms.forEach((form) => {
      new FormData(form).forEach((value, key) => {
        if (value === '') return;
        params.append(key, value);
      });
    });

    // Price needs two corrections after the raw FormData pass:
    //
    // 1. The visible fields carry a formatted value ("₹2,00,000"), which is
    //    what FormData picks up — Shopify's filter.v.price.* params take a
    //    plain number, so a formatted string is simply ignored and the price
    //    filter silently does nothing. Each param is rewritten to digits.
    // 2. Those fields always hold a value (defaulting to the full
    //    ₹10,000–₹5,00,000 span), so they'd be submitted alongside every
    //    other filter and cut off anything priced outside that span even
    //    though the customer never touched the price control. A range at its
    //    full extent means "no price constraint", so its params are dropped
    //    — which also means dragging back out to the full span clears the
    //    price filter again.
    activeForms.forEach((form) => {
      form.querySelectorAll('[data-plp-price-range]').forEach((range) => {
        const min = range.querySelector('[data-plp-price-handle="min"]');
        const max = range.querySelector('[data-plp-price-handle="max"]');
        if (!min || !max) return;

        const atFullSpan =
          Number(min.value) <= Number(min.min) && Number(max.value) >= Number(max.max);

        range.querySelectorAll('[data-plp-price-input]').forEach((input) => {
          if (!input.name) return;
          if (atFullSpan) {
            params.delete(input.name);
          } else {
            params.set(input.name, String(this.parsePrice(input.value)));
          }
        });
      });
    });

    const existingSort = this.currentParams().get('sort_by');
    if (existingSort && !params.has('sort_by')) params.set('sort_by', existingSort);

    this.render(params);
  }

  renderFromUrl(href) {
    const url = new URL(href, window.location.origin);
    this.render(new URLSearchParams(url.search));
  }

  async render(params) {
    params.delete('page');
    const search = params.toString();
    const url = `${window.location.pathname}?section_id=${this.sectionId}${search ? `&${search}` : ''}`;

    this.classList.add('plp--loading');
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const text = await response.text();
      this.swap(text);

      const newUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
      window.history.pushState({ search }, '', newUrl);
    } catch (error) {
      if (error.name !== 'AbortError') console.error('[plp] failed to update results', error);
    } finally {
      this.classList.remove('plp--loading');
    }
  }

  swap(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = parsed.querySelector('plp-collection') || parsed.getElementById(this.id);
    if (!incoming) return;

    const openState = Array.from(this.querySelectorAll('[data-plp-facet]')).map((node) => node.open);
    const activeId = document.activeElement ? document.activeElement.id : null;
    const openDrawer = this.querySelector('.plp-drawer[open]');
    const openDrawerName = openDrawer ? openDrawer.dataset.plpDrawer : null;

    this.innerHTML = incoming.innerHTML;

    this.querySelectorAll('[data-plp-facet]').forEach((node, index) => {
      if (openState[index] !== undefined) node.open = openState[index];
    });

    if (activeId) {
      const restored = this.querySelector(`#${CSS.escape(activeId)}`);
      if (restored) restored.focus();
    }

    if (openDrawerName) this.openDrawer(openDrawerName);

    this.bindPriceRanges();
    this.bindInfiniteScroll();
    this.bindScrollPerformance();
    this.syncAppliedOverflow();
  }

  /* ---------------------------------------------------------------- *
   * Drawers (mobile)
   * ---------------------------------------------------------------- */

  openDrawer(name) {
    const drawer = this.querySelector(`[data-plp-drawer="${name}"]`);
    if (!drawer) return;
    drawer.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    const focusable = drawer.querySelector('button, input, a[href]');
    if (focusable) focusable.focus();
  }

  closeDrawers() {
    this.querySelectorAll('.plp-drawer[open]').forEach((drawer) => drawer.removeAttribute('open'));
    document.body.style.overflow = '';
  }

  /* ---------------------------------------------------------------- *
   * Infinite scroll (Figma shows a loader below the last row)
   * ---------------------------------------------------------------- */

  bindInfiniteScroll() {
    if (this.observer) this.observer.disconnect();
    const loader = this.querySelector('[data-plp-loader]');
    if (!loader) return;

    loader.hidden = false;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.loadNextPage(loader);
        });
      },
      { rootMargin: '400px' }
    );
    this.observer.observe(loader);
  }

  /* Card hover animates layout-affecting properties (width/padding on the
     video-consult pill, height/margin-top on "Book a Consultation" — see
     plp.css) to match the Figma reveal exactly. Scrolling the grid with a
     mouse wheel while the cursor stays still drags a new card under it every
     frame, so the browser keeps re-triggering those transitions on whichever
     card the cursor now sits over — the cause of the scroll feeling
     stepped/janky rather than the transitions themselves. Suppressing
     pointer events on the grid for the duration of the scroll stops :hover
     from being re-evaluated mid-scroll without changing the hover animation
     or its timing once scrolling stops. */
  bindScrollPerformance() {
    if (this.scrollPerfTarget) {
      this.scrollPerfTarget.removeEventListener('scroll', this.handleGridScroll);
    }

    const grid = this.querySelector('[data-plp-grid]');
    if (!grid) return;

    this.scrollPerfTarget = window;

    this.handleGridScroll = () => {
      grid.classList.add('plp-grid--scrolling');
      clearTimeout(this.scrollPerfTimeout);
      this.scrollPerfTimeout = setTimeout(() => grid.classList.remove('plp-grid--scrolling'), 150);
    };

    this.scrollPerfTarget.addEventListener('scroll', this.handleGridScroll, { passive: true });
  }

  async loadNextPage(loader) {
    if (loader.dataset.loading === 'true') return;
    const nextUrl = loader.dataset.nextUrl;
    if (!nextUrl) return;

    loader.dataset.loading = 'true';
    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = this.querySelector('[data-plp-grid]');
      const incomingGrid = parsed.querySelector('[data-plp-grid]');
      if (grid && incomingGrid) grid.append(...incomingGrid.children);

      const incomingLoader = parsed.querySelector('[data-plp-loader]');
      if (incomingLoader && incomingLoader.dataset.nextUrl) {
        loader.dataset.nextUrl = incomingLoader.dataset.nextUrl;
      } else {
        this.observer.disconnect();
        loader.remove();
      }
    } catch (error) {
      console.error('[plp] failed to load the next page', error);
    } finally {
      loader.dataset.loading = 'false';
    }
  }
}

if (!customElements.get('plp-collection')) {
  customElements.define('plp-collection', PlpCollection);
}

window.addEventListener('popstate', () => {
  const element = document.querySelector('plp-collection');
  if (element) element.render(new URLSearchParams(window.location.search));
});
